import axios from './axiosConfig'

// Response type - includes isLocked computed by backend
export interface MeaningSection {
    name: string
    content: string | null
    isLocked: boolean
}

// Input type for saving - no isLocked (backend computes it)
export interface MeaningSectionInput {
    name: string
    content: string
}

// Flexible image categories - any key like "meme", "mood", "vibe", etc.
export interface MeaningImages {
    [category: string]: string[]
}

// Response type - includes isLocked computed by backend
export interface MeaningContent {
    keywords: string[]
    overview: string
    sections: MeaningSection[]
    images?: MeaningImages
    imagesLocked?: boolean
}

// Input type for saving - no isLocked fields
export interface MeaningContentInput {
    keywords: string[]
    overview: string
    sections: MeaningSectionInput[]
    images?: MeaningImages
}

export interface GuidebookMeaning {
    id: string
    slug: string
    name: string
    category: string
    order: number
    imagePath: string | null
    upright: MeaningContent
    reversed: MeaningContent | null
}

export interface UpdateGuidebookMeaningInput {
    name?: string
    slug?: string
    category?: string
    order?: number
    imagePath?: string | null
    upright?: MeaningContentInput
    reversed?: MeaningContentInput | null
}

// Public: Get card overviews by names (for daily spread showcase modal)
export interface CardOverview {
    name: string
    overview: string
    overviewReverse: string
}

export const getCardOverviews = async (names: string[]): Promise<CardOverview[]> => {
    const response = await axios.post('/guidebook/overviews', { names })
    return response.data.overviews
}

// Public: Get all meanings (content filtered based on user's subscription)
export const getGuidebookMeanings = async (): Promise<{ meanings: GuidebookMeaning[]; hasFullAccess: boolean }> => {
    const response = await axios.get('/guidebook')
    return response.data
}

// Public: Get meanings by category
export const getGuidebookByCategory = async (category: string): Promise<{ category: string; meanings: GuidebookMeaning[]; hasFullAccess: boolean }> => {
    const response = await axios.get(`/guidebook/category/${category}`)
    return response.data
}

// Public: Get single meaning by slug
export const getGuidebookMeaning = async (slug: string): Promise<{ meaning: GuidebookMeaning; hasFullAccess: boolean }> => {
    const response = await axios.get(`/guidebook/${slug}`)
    return response.data
}

// Get all meanings for admin (full content, no filtering)
export const getAllGuidebookMeanings = async (): Promise<GuidebookMeaning[]> => {
    const response = await axios.get('/guidebook/admin/all')
    return response.data
}

// Update a guidebook meaning (admin only)
export const updateGuidebookMeaning = async (
    slug: string,
    input: UpdateGuidebookMeaningInput
): Promise<GuidebookMeaning> => {
    const response = await axios.put(`/guidebook/${slug}`, input)
    return response.data
}

export interface CreateGuidebookMeaningInput {
    slug: string
    name: string
    category: string
    order: number
    imagePath?: string | null
    upright: MeaningContentInput
    reversed?: MeaningContentInput | null
}

// Create a new guidebook meaning (admin only)
export const createGuidebookMeaning = async (
    input: CreateGuidebookMeaningInput
): Promise<GuidebookMeaning> => {
    const response = await axios.post('/guidebook', input)
    return response.data
}

// Delete a guidebook meaning (admin only)
export const deleteGuidebookMeaning = async (slug: string): Promise<void> => {
    await axios.delete(`/guidebook/${slug}`)
}

// Helper: Convert images to markdown-style text (separate from meaning content)
export const imagesToMarkdown = (images: MeaningImages | undefined): string => {
    if (!images) return ''
    const lines: string[] = []
    for (const [category, urls] of Object.entries(images)) {
        if (urls && urls.length > 0) {
            lines.push(`${category} images:`)
            for (const url of urls) {
                lines.push(`- ${url}`)
            }
            lines.push('')
        }
    }
    return lines.join('\n').trim()
}

// Helper: Parse markdown-style text to images object
export const markdownToImages = (text: string): MeaningImages | undefined => {
    if (!text.trim()) return undefined
    const lines = text.split('\n')
    const images: MeaningImages = {}
    let currentCategory: string | null = null

    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        // Image category header (matches "meme images:", "mood images:", etc.)
        const imageHeaderMatch = trimmed.toLowerCase().match(/^(\w+)\s+images:$/)
        if (imageHeaderMatch) {
            currentCategory = imageHeaderMatch[1]
            continue
        }

        // Image item
        if (currentCategory && trimmed.startsWith('-')) {
            if (!images[currentCategory]) {
                images[currentCategory] = []
            }
            images[currentCategory].push(trimmed.slice(1).trim())
        }
    }

    return Object.keys(images).length > 0 ? images : undefined
}

// Helper: Convert MeaningContent to markdown-style text (without images)
export const meaningToMarkdown = (content: MeaningContent): string => {
    const lines: string[] = []

    // Keywords
    lines.push(`keywords: ${content.keywords.join(', ')}`)
    lines.push('')

    // Overview
    lines.push(`overview: ${content.overview}`)
    lines.push('')

    // Sections (only include unlocked sections with content)
    for (const section of content.sections) {
        if (section.content !== null) {
            // "journal questions" section: output as plain lines (one question per line)
            if (section.name.toLowerCase() === 'journal questions') {
                lines.push(`(${section.name}):`)
                const questions = section.content.split('\n').filter(q => q.trim())
                for (const q of questions) {
                    lines.push(q)
                }
                lines.push('')
            } else {
                lines.push(`(${section.name}): ${section.content}`)
                lines.push('')
            }
        }
    }

    return lines.join('\n').trim()
}

// Helper: Parse markdown-style text to MeaningContentInput (without images - those are separate)
// Returns input type without isLocked - backend computes that based on FREE_SECTIONS
export const markdownToMeaning = (text: string): MeaningContentInput => {
    const lines = text.split('\n')
    const result: MeaningContentInput = {
        keywords: [],
        overview: '',
        sections: [],
    }

    let currentSection: string | null = null
    let currentSectionItems: string[] = []

    const flushCurrentSection = () => {
        if (currentSection && currentSectionItems.length > 0) {
            result.sections.push({
                name: currentSection,
                content: currentSectionItems.join('\n'),
            })
        }
        currentSection = null
        currentSectionItems = []
    }

    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        // Keywords
        if (trimmed.toLowerCase().startsWith('keywords:')) {
            flushCurrentSection()
            const keywordsStr = trimmed.slice('keywords:'.length).trim()
            result.keywords = keywordsStr.split(',').map(k => k.trim()).filter(k => k)
            continue
        }

        // Overview
        if (trimmed.toLowerCase().startsWith('overview:')) {
            flushCurrentSection()
            result.overview = trimmed.slice('overview:'.length).trim()
            continue
        }

        // Section header: (name): or (name): content
        const sectionMatch = trimmed.match(/^\(([^)]+)\):\s*(.*)$/)
        if (sectionMatch) {
            flushCurrentSection()
            const sectionName = sectionMatch[1].toLowerCase()
            const sectionContent = sectionMatch[2]

            // Start tracking this section - even if it has inline content,
            // we want to collect any accidental continuation lines
            currentSection = sectionName
            if (sectionContent) {
                currentSectionItems.push(sectionContent)
            }
            continue
        }

        // Continuation line for current section (handles accidental extra lines)
        if (currentSection) {
            currentSectionItems.push(trimmed)
            continue
        }
    }

    // Flush any remaining section
    flushCurrentSection()

    return result
}

